// Painting advice articles. To add a post, copy an entry and give it a unique
// slug. Block types: h2, p, list (items array), quote.
export const posts = [
  {
    slug: "choosing-paint-colours",
    title: "How to Choose Paint Colours You'll Still Love in Five Years",
    excerpt: "Trends come and go. These practical steps — light, undertones and real samples — help you pick colours that last longer than the trend cycle.",
    date: "2026-05-12",
    readTime: "5 min read",
    category: "Colour",
    image: "swatchDeck",
    blocks: [
      { t: "p", text: "The most expensive paint colour mistake is not the paint — it is repainting a whole room because the colour felt wrong after a week. A slower, more deliberate selection process almost always beats a fast one." },
      { t: "h2", text: "Start with light, not the swatch wall" },
      { t: "p", text: "North-facing rooms in the Lower Mainland receive cool, grey light for much of the year, which flattens warm colours and cools down neutrals. South-facing rooms do the opposite, intensifying warmth in the afternoon. Before you fall in love with a chip, note which way your windows face and when you actually use the room." },
      { t: "h2", text: "Understand undertones" },
      { t: "p", text: "Almost every neutral leans somewhere: pink, yellow, green, blue or violet. Put your candidate next to a pure white sheet of paper and the undertone reveals itself. Clashing undertones — not clashing colours — are what make a room feel 'off'." },
      { t: "h2", text: "Test on the actual wall" },
      { t: "list", items: [
        "Paint sample patches at least 60 cm square on two different walls",
        "Look at them in morning, afternoon and evening light, and with lamps on",
        "Live with the patches for at least a few days before deciding",
        "Check the colour against fixed elements: flooring, counters, tile and large furniture",
      ] },
      { t: "h2", text: "Think in sightlines" },
      { t: "p", text: "Stand in one room and note every other room you can see. Colours in connected spaces should relate — they do not need to match, but they should not argue. Open-plan main floors usually work best with one main wall colour and bolder colour saved for contained rooms." },
      { t: "p", text: "Our colour palette tool is a good way to explore coordinated palettes, but remember that screens never match real paint exactly — always confirm with a physical sample. When you are ready, we are happy to talk colours through during your estimate." },
    ],
  },
  {
    slug: "preparing-room-for-interior-painting",
    title: "How to Prepare a Room for Interior Painting",
    excerpt: "What to do before the painters arrive — and what a professional crew should be handling for you.",
    date: "2026-04-28",
    readTime: "4 min read",
    category: "Interior",
    image: "painterPrep",
    blocks: [
      { t: "p", text: "Good preparation is the difference between a paint job that looks great for a decade and one that shows flaws within a year. Here is how the work divides between you and your painting crew." },
      { t: "h2", text: "What homeowners should do" },
      { t: "list", items: [
        "Remove small, fragile and personal items from shelves and surfaces",
        "Take down curtains, blinds and wall hangings you want kept safe",
        "Clear closet contents if closets are being painted",
        "Arrange a plan for pets — open doors and wet paint are a risky mix",
        "Point out any known issues: past leaks, recurring cracks, loose outlets",
      ] },
      { t: "h2", text: "What your painters should handle" },
      { t: "p", text: "A professional crew moves and covers furniture, protects floors, removes or masks switch plates, fills minor holes, sands repairs smooth, spot-primes, and masks every edge that should not get paint. If a quote skips preparation to come in cheap, ask what exactly is included — the answer tells you a lot." },
      { t: "h2", text: "The preparation sequence that matters" },
      { t: "list", items: [
        "Wash or dust surfaces so paint bonds to the wall, not the grime",
        "Fill holes and cracks, then sand them flush",
        "Spot-prime repairs and stains so they do not flash through the finish",
        "Caulk gaps at trim joints for sharp, sealed lines",
        "Mask and protect before the first drop of finish coat is opened",
      ] },
      { t: "p", text: "Every North Ember Painting estimate spells out the preparation included in your project, so you know exactly what will happen before colour goes on the wall." },
    ],
  },
  {
    slug: "exterior-paint-maintenance-bc-coastal-climate",
    title: "Exterior Paint Maintenance in BC's Coastal Climate",
    excerpt: "Rain, mildew, salt air and low winter sun: how coastal weather treats exterior paint, and the simple habits that add years to a finish.",
    date: "2026-04-10",
    readTime: "6 min read",
    category: "Exterior",
    image: "exteriorPnw",
    blocks: [
      { t: "p", text: "Exterior paint in Delta and the surrounding coastal communities works harder than almost anywhere else in Canada. Long wet seasons, mild temperatures that let mildew thrive, and salt air near the water all shorten the life of a finish — unless you maintain it." },
      { t: "h2", text: "Know your exposures" },
      { t: "p", text: "South and west walls take the most UV and weather-driven wear. North walls and shaded areas stay damp longer and grow mildew. Walk your home once a year and look at each elevation separately — they age at different speeds, and catching the worst one early is far cheaper than waiting for all four." },
      { t: "h2", text: "An annual maintenance habit" },
      { t: "list", items: [
        "Gently wash siding each spring to remove dirt and mildew before it etches the finish",
        "Keep soil, mulch and plants from touching the siding",
        "Check caulking around windows, doors and trim joints and repair cracks early",
        "Trim back vegetation so walls can dry after rain",
        "Touch up chips and scratches promptly — bare wood wicks moisture",
      ] },
      { t: "h2", text: "Signs it is time to repaint" },
      { t: "p", text: "Chalking (a powdery residue on your hand), fading on sun-exposed walls, cracking or peeling at joints, and caulk that has pulled away are all early warnings. Peeling that exposes bare wood is the urgent one — moisture gets in quickly from there." },
      { t: "h2", text: "When in doubt, get an honest assessment" },
      { t: "p", text: "Not every weathered home needs a full repaint. Sometimes targeted preparation and a maintenance coat on the exposed elevations buys several more years. We will always tell you which situation you are in — request an estimate and we will take a proper look." },
    ],
  },
  {
    slug: "cabinet-painting-basics",
    title: "Cabinet Painting Basics: What to Know Before You Refinish",
    excerpt: "Refinishing cabinets can transform a kitchen for a fraction of replacement cost — if the preparation is done right. Here is what to expect.",
    date: "2026-03-22",
    readTime: "5 min read",
    category: "Cabinets",
    image: "kitchenCabinets",
    blocks: [
      { t: "p", text: "Kitchen cabinets dominate the look of the room, and refinishing them is one of the highest-impact updates available without a renovation. But cabinets are also the most demanding surfaces in a home: grease, steam, fingernails and constant handling." },
      { t: "h2", text: "Are your cabinets good candidates?" },
      { t: "p", text: "Solid wood and quality veneer cabinets in sound condition refinish beautifully. Swollen particleboard, delaminating thermofoil and water-damaged boxes are harder cases — sometimes repairable, sometimes not. An honest assessment at the start avoids disappointment later." },
      { t: "h2", text: "Preparation is most of the job" },
      { t: "list", items: [
        "Degreasing — kitchen residue is invisible until paint will not stick to it",
        "Sanding to dull the existing finish and give primers a surface to grip",
        "Bonding primer suited to the existing coating",
        "Thin, even topcoats with proper drying time between them",
        "Respect for curing time before doors go back into daily service",
      ] },
      { t: "h2", text: "Living through the project" },
      { t: "p", text: "Plan for several days to a week, depending on the size of the kitchen. Doors and drawer fronts come off, so you will have open shelving for a while. Most households keep using the kitchen with minor adjustments — we sequence the work to keep disruption reasonable." },
      { t: "h2", text: "Hardware: the finishing touch" },
      { t: "p", text: "Fresh paint is the perfect moment to update knobs and pulls. If new hardware needs different hole spacing, mention it early so filling and drilling can be planned into the job." },
    ],
  },
  {
    slug: "choose-painting-contractor-delta",
    title: "How to Choose a Painting Contractor in Delta, BC",
    excerpt: "Insurance, written estimates, preparation details and communication — a practical checklist for hiring a painter you can trust.",
    date: "2026-03-05",
    readTime: "5 min read",
    category: "Hiring",
    image: "neighbourhoodDelta",
    blocks: [
      { t: "p", text: "Painting is one of the most accessible trades to enter, which means the quality gap between contractors is wide. A little structure in how you compare quotes protects you from most of the common problems." },
      { t: "h2", text: "The essentials to verify" },
      { t: "list", items: [
        "Insurance — ask directly and do not proceed without a clear answer",
        "A written estimate that lists scope, preparation, coats and materials approach",
        "Local presence — a contractor based near you is easier to reach if something needs attention later",
        "Clear, prompt communication during the quote stage; it rarely improves after you hire",
      ] },
      { t: "h2", text: "Compare preparation, not just price" },
      { t: "p", text: "Two quotes for the 'same' job can differ mostly in preparation: washing, scraping, sanding, filling, caulking and priming. Preparation is where durability comes from, so ask each contractor to describe exactly what preparation their price includes." },
      { t: "h2", text: "Questions worth asking" },
      { t: "list", items: [
        "Who will actually be in my home, and who supervises the work?",
        "How will you protect my floors, furniture and landscaping?",
        "What happens if I find an issue after the job is done?",
        "How do you handle weather delays on exterior work?",
      ] },
      { t: "h2", text: "Red flags" },
      { t: "p", text: "Be cautious of quotes given without seeing the project, pressure to decide immediately, requests for large upfront payments, and any contractor unwilling to put scope in writing." },
      { t: "p", text: "North Ember Painting is based in North Delta, fully insured, and provides written estimates for every project. If you are comparing painters in Delta, Ladner, Tsawwassen or Surrey, we would be glad to earn a place on your shortlist." },
    ],
  },
];

export const getPost = (slug) => posts.find((p) => p.slug === slug);
